import React, { useState, useEffect } from "react";
import axios from "axios";
import { Auth } from "aws-amplify";
import Navbar from "../common/Navbar";
import { awsUrl, scraperApiKey } from "../../config";
import "./DataExtraction.css";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DC","DE","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
  "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY",
];

const TOOLS = [
  { value: "EMOD", label: "EMOD" },
  { value: "OSHA", label: "OSHA" },
  { value: "SOS", label: "SOS" },
];

function StatusBadge({ status }) {
  const colorMap = {
    PENDING: "#f0ad4e",
    PROCESSING: "#5bc0de",
    SUCCESS: "#5cb85c",
    FAILED: "#d9534f",
  };
  return (
    <span
      className="de-status-badge"
      style={{ background: colorMap[status] || "#999" }}
    >
      {status}
    </span>
  );
}

function JobCard({ job, onDelete, scraperUrl, apiKey }) {
  const outputFiles = job.outputFiles || [];

  return (
    <div className={`de-job-card de-job-${job.status?.toLowerCase()}`}>
      <div className="de-job-header">
        <div className="de-job-tool">{job.tool}</div>
        <StatusBadge status={job.status} />
      </div>
      <div className="de-job-details">
        <p><strong>Client:</strong> {job.clientName}</p>
        <p><strong>State:</strong> {job.state}</p>
        {job.fein && <p><strong>FEIN:</strong> {job.fein}</p>}
        <p className="de-job-time">{new Date(job.createdAt).toLocaleString()}</p>
      </div>

      {job.status === "SUCCESS" && outputFiles.length > 0 && (
        <div className="de-downloads">
          <p className="de-downloads-label">📁 {outputFiles.length} file(s):</p>
          {outputFiles.map((fname, i) => (
            <a
              key={i}
              href={`${scraperUrl}/api/jobs/${job.jobId}/download/${fname}?api_key=${apiKey}`}
              target="_blank"
              rel="noopener noreferrer"
              className="de-download-btn"
            >
              ⬇ {fname}
            </a>
          ))}
        </div>
      )}

      {job.status === "FAILED" && job.errorMessage && (
        <p className="de-error-msg">{job.errorMessage}</p>
      )}

      <button onClick={() => onDelete(job.jobId)} className="de-job-delete-btn">
        🗑 Delete
      </button>
    </div>
  );
}

export default function DataExtraction() {
  const [clientName, setClientName] = useState("");
  const [fein, setFein] = useState("");
  const [state, setState] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // ====== Auth State ======
  const [isAuthorized, setIsAuthorized] = useState(null);

  // ====== Server Control State ======
  const [serverStatus, setServerStatus] = useState("checking"); // checking, stopped, starting, running, stopping
  const [serverIp, setServerIp] = useState(null);
  const [serverHealth, setServerHealth] = useState(null);
  const [serverLoading, setServerLoading] = useState(false);

  // Derive dynamic scraper URL based on whether server has IP assigned
  const dynamicScraperUrl = serverIp ? `http://${serverIp}` : "";
  const reqHeaders = { "x-api-key": scraperApiKey };

  // Load jobs and server status on mount
  useEffect(() => {
    checkAuthorization();
  }, []);

  async function checkAuthorization() {
    try {
      const session = await Auth.currentSession();
      const groups = session.getIdToken().payload["cognito:groups"] || [];
      if (groups.includes("DataExtraction")) {
        setIsAuthorized(true);
        checkServerStatus();
      } else {
        setIsAuthorized(false);
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      setIsAuthorized(false);
    }
  }

  // When server is running, fetch jobs
  useEffect(() => {
    if (serverStatus === "running" && serverIp) {
      fetchJobs();
    }
  }, [serverStatus, serverIp]);

  // ====== Server Control Methods ======
  async function checkServerStatus() {
    setServerLoading(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const header = { headers: { Authorization: `Bearer ${token}` } };

      const res = await axios.get(`${awsUrl}/api/scraper/status`, header);
      const { status, ip, health } = res.data;
      setServerStatus(status);
      setServerIp(ip);
      setServerHealth(health);
      if (status === "running" && !health) {
        // Retry logic or just wait for next poll
      }
    } catch (err) {
      console.error("Failed to fetch server status:", err);
      // Fallback in case lambda is not deployed yet
      setServerStatus("stopped");
      setServerIp(null);
    } finally {
      setServerLoading(false);
    }
  }

  async function startServer() {
    setServerLoading(true);
    setServerStatus("starting");
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const header = { headers: { Authorization: `Bearer ${token}` } };

      const res = await axios.get(`${awsUrl}/api/scraper/start`, header);
      // It might take time to get an IP depending on EC2 boot time
      if (res.data.status === "running") {
        setServerStatus("running");
        setServerIp(res.data.ip);
        fetchJobs();
      } else {
        // Poll status every 3 seconds until running
        const interval = setInterval(async () => {
          try {
            const checkSession = await Auth.currentSession();
            const checkToken = checkSession.getIdToken().getJwtToken();
            const checkHeader = { headers: { Authorization: `Bearer ${checkToken}` } };

            const check = await axios.get(`${awsUrl}/api/scraper/status`, checkHeader);
            if (check.data.status === "running" && check.data.ip) {
              setServerStatus("running");
              setServerIp(check.data.ip);
              setServerHealth(check.data.health);
              clearInterval(interval);
              fetchJobs();
            }
          } catch (e) { console.error("Poll error", e); }
        }, 3000);
      }
    } catch (err) {
      console.error("Start server failed:", err);
      setError("Failed to start server.");
      setServerStatus("stopped");
    } finally {
      setServerLoading(false);
    }
  }

  async function stopServer() {
    if (!window.confirm("Are you sure you want to stop the extraction server? Any running jobs will fail.")) return;
    setServerLoading(true);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const header = { headers: { Authorization: `Bearer ${token}` } };

      await axios.get(`${awsUrl}/api/scraper/stop`, header);
      setServerStatus("stopping");
      setServerIp(null);
      setServerHealth(null);
      setJobs([]); // Clear jobs from UI
      setTimeout(() => checkServerStatus(), 5000);
    } catch (err) {
      console.error("Stop server failed:", err);
      setError("Failed to stop server.");
    } finally {
      setServerLoading(false);
    }
  }

  // ====== Job Management Methods ======
  async function fetchJobs() {
    setLoading(true);
    try {
      const res = await axios.get(dynamicScraperUrl + "/api/jobs?limit=30", { headers: reqHeaders });
      setJobs(res.data || []);
    } catch (err) {
      console.error("Failed to load jobs:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteJob(jobId) {
    if (!window.confirm("Are you sure you want to delete this job and its output files?")) return;
    try {
      await axios.delete(`${dynamicScraperUrl}/api/jobs/${jobId}`, { headers: reqHeaders });
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
      setSuccessMsg("Job deleted successfully.");
    } catch (err) {
      console.error("Failed to delete job:", err);
      setError("Failed to delete job.");
    }
  }

  async function handleCleanup() {
    if (!window.confirm("This will delete jobs older than 2 days. Continue?")) return;
    try {
      const res = await axios.delete(`${dynamicScraperUrl}/api/jobs/cleanup`, { headers: reqHeaders });
      setSuccessMsg(res.data.message || "Cleanup successful.");
      await fetchJobs();
    } catch (err) {
      console.error("Cleanup failed:", err);
      setError("Cleanup failed.");
    }
  }

  function handleToolToggle(toolVal) {
    setSelectedTools((prev) => {
      const next = prev.includes(toolVal)
        ? prev.filter((t) => t !== toolVal)
        : [...prev, toolVal];
      setSelectAll(next.length === TOOLS.length);
      return next;
    });
  }

  function handleSelectAll(e) {
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectedTools(checked ? TOOLS.map((t) => t.value) : []);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!clientName.trim()) return setError("Company name is required.");
    if (!state) return setError("Please select a state.");
    if (selectedTools.length === 0) return setError("Please select at least one tool.");

    setSubmitting(true);

    try {
      for (const tool of selectedTools) {
        await axios.post(dynamicScraperUrl + "/api/extract", {
          tool,
          clientName: clientName.trim(),
          state,
          fein: fein.trim(),
        }, { headers: reqHeaders });
      }

      setSuccessMsg(
        `✅ ${selectedTools.length} job(s) submitted! Click "Refresh" to check status.`
      );

      // Reload jobs list
      await fetchJobs();
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.response?.data?.detail || "Failed to create job. Is the API running?");
    } finally {
      setSubmitting(false);
    }
  }

  if (isAuthorized === null) {
    return (
      <div className="de-page">
        <Navbar />
        <div className="de-container" style={{ textAlign: "center", marginTop: "50px" }}>
          <h2>Checking authorization...</h2>
        </div>
      </div>
    );
  }

  if (isAuthorized === false) {
    return (
      <div className="de-page">
        <Navbar />
        <div className="de-container" style={{ textAlign: "center", marginTop: "50px", color: "#d9534f" }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Data Extraction page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="de-page">
      <Navbar />
      <div className="de-container">
        <div className="de-header">
          <h1>Data Extraction</h1>
          <p>Run EMOD, OSHA, and SOS scrapers for any company</p>
        </div>

        {/* Server Control Panel */}
        <div className="de-server-panel">
          <div className="de-server-info">
            <h3>EC2 Server Status</h3>
            <div className="de-server-badges">
              <span className={`de-status-badge de-server-${serverStatus}`}>
                {serverStatus.toUpperCase()}
              </span>
              {serverIp && <span className="de-server-ip">IP: {serverIp}</span>}
              
              {serverHealth && (
                <>
                  <span className="de-server-metric">
                    ⏳ Uptime: {Math.floor(serverHealth.uptime_seconds / 3600)}h {Math.floor((serverHealth.uptime_seconds % 3600) / 60)}m
                  </span>
                  <span className="de-server-metric">
                    🧠 RAM: {serverHealth.memory_used_mb.toFixed(0)} MB ({serverHealth.memory_percent}%)
                  </span>
                  <span className="de-server-metric">
                    ⚙️ CPU: {serverHealth.cpu_percent}%
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="de-server-actions">
            {serverStatus === "stopped" || serverStatus === "stopping" ? (
              <button className="de-server-start-btn" onClick={startServer} disabled={serverLoading || serverStatus === "stopping"}>
                {serverLoading || serverStatus === "stopping" ? "Starting..." : "▶ Start Server"}
              </button>
            ) : (
              <button className="de-server-stop-btn" onClick={stopServer} disabled={serverLoading || serverStatus === "starting"}>
                {serverLoading ? "Stopping..." : "⏹ Stop Server"}
              </button>
            )}
          </div>
        </div>

        <div className={`de-content ${serverStatus !== "running" ? "de-content-disabled" : ""}`}>
          {/* Input Form */}
          <div className="de-form-card">
            <form onSubmit={handleSubmit}>
              <div className="de-form-group">
                <label htmlFor="de-company">Company Name *</label>
                <input
                  id="de-company"
                  type="text"
                  placeholder="Enter company name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                />
              </div>

              <div className="de-form-row">
                <div className="de-form-group">
                  <label htmlFor="de-fein">FEIN</label>
                  <input
                    id="de-fein"
                    type="text"
                    placeholder="e.g. 12-3456789"
                    value={fein}
                    onChange={(e) => setFein(e.target.value)}
                  />
                </div>

                <div className="de-form-group">
                  <label htmlFor="de-state">State *</label>
                  <select
                    id="de-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="de-form-group">
                <label>Tools *</label>
                <div className="de-tool-grid">
                  <label className="de-tool-option de-select-all">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                    <span>Select All</span>
                  </label>
                  {TOOLS.map((tool) => (
                    <label key={tool.value} className="de-tool-option">
                      <input
                        type="checkbox"
                        checked={selectedTools.includes(tool.value)}
                        onChange={() => handleToolToggle(tool.value)}
                      />
                      <span>{tool.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {error && <div className="de-error">{error}</div>}
              {successMsg && <div className="de-success">{successMsg}</div>}

              <button
                type="submit"
                className="de-submit-btn"
                disabled={submitting || serverStatus !== "running"}
              >
                {submitting ? "Submitting..." : (serverStatus === "running" ? "Run Extraction" : "Server Not Running")}
              </button>
            </form>
          </div>

          {/* Jobs List */}
          <div className="de-results">
            <div className="de-results-header">
              <h2>Extraction Jobs</h2>
              <div className="de-results-actions">
                <button
                  className="de-cleanup-btn"
                  onClick={handleCleanup}
                  disabled={loading}
                >
                  Clean Old Data
                </button>
                <button
                  className="de-refresh-btn"
                  onClick={fetchJobs}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>
            </div>

            {jobs.length === 0 && !loading && (
              <p className="de-empty">No jobs yet. Submit an extraction above.</p>
            )}

            <div className="de-jobs-grid">
              {jobs.map((job) => (
                <JobCard key={job.jobId} job={job} onDelete={handleDeleteJob} scraperUrl={dynamicScraperUrl} apiKey={scraperApiKey} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
